// Camada única de acesso à API: URL base, token, limite de tempo e cancelamento num lugar só.

const BASE_URL = process.env.REACT_APP_BASE_URL || "";
const TEMPO_LIMITE_MS = 15000;

export class ApiError extends Error {
  constructor(mensagem, status) {
    super(mensagem);
    this.name = "ApiError";
    this.status = status;
  }
}

const tokenDaSessao = () => {
  try {
    return JSON.parse(sessionStorage.getItem("user") || "{}").access_token || null;
  } catch {
    return null;
  }
};

// Monta a query ignorando valores vazios (null/undefined/"").
export const montarQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([chave, valor]) => {
    if (valor !== undefined && valor !== null && valor !== "") query.append(chave, valor);
  });
  const texto = query.toString();
  return texto ? `?${texto}` : "";
};

/**
 * Faz a requisição e devolve o JSON (ou null quando não há corpo).
 * - autenticar: envia o token da sessão no header "Authentication", se houver.
 * - signal: cancelamento de quem chamou (troca de tela/página). Cancelado → AbortError.
 * - Servidor travado: aborta em 15s e lança ApiError com status 0.
 */
export const apiFetch = async (caminho, { method = "GET", body, autenticar = false, signal } = {}) => {
  const controller = new AbortController();
  let estourouTempo = false;
  const timeout = setTimeout(() => {
    estourouTempo = true;
    controller.abort();
  }, TEMPO_LIMITE_MS);
  const cancelar = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener("abort", cancelar);

  const headers = { "Content-Type": "application/json" };
  const token = autenticar ? tokenDaSessao() : null;
  if (token) headers.Authentication = token;

  try {
    const response = await fetch(`${BASE_URL}${caminho}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
    if (!response.ok) throw new ApiError(`Erro ${response.status} em ${caminho}`, response.status);
    const texto = await response.text();
    return texto ? JSON.parse(texto) : null;
  } catch (error) {
    if (estourouTempo && !signal?.aborted) throw new ApiError(`Tempo esgotado em ${caminho}`, 0);
    throw error;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", cancelar);
  }
};

export const foiCancelado = (error, signal) => error?.name === "AbortError" || Boolean(signal?.aborted);

// Endpoints públicos usados nas telas do cliente.
export const apiPublica = {
  listarRestaurantes: (signal) => apiFetch("/api/v1/public/restaurantes", { autenticar: true, signal }),
  buscarRestaurante: (restauranteId, signal) =>
    apiFetch(`/api/v1/public/restaurantes/${restauranteId}`, { signal }),
  cardapio: (restauranteId, { page, size, idCategoria, nome }, signal) =>
    apiFetch(
      `/api/v1/public/restaurantes/${restauranteId}/cardapio${montarQuery({ page, size, idCategoria, nome })}`,
      { signal }
    ),
  categorias: (restauranteId, signal) =>
    apiFetch(`/api/v1/public/categorias${montarQuery({ restauranteId })}`, { signal }),
};
